import { Box, Card, CardActionArea, CardActions, CardContent, CardHeader, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowDownwardOutlined, ArrowUpwardOutlined } from '@material-ui/icons';
import { NumericFormat } from 'react-number-format';
import { darkenHexColor } from '../../dwi-functions';
import Chart from 'react-apexcharts'
import { applyApexBrandGradient } from '../../system/apex-gradient';
import { renderTrendTooltip } from '../../system/apex-tooltip';
import CurrencyFormat from 'react-currency-format';
import { getReportSurfaceSx, reportColors, styleReportChart } from '../../views/shared/report-design';
import { getExecutiveGlassCardSx } from '../../views/shared/reportSurfaceStyles';

const safeNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const parseSparklineX = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
};

const formatSparklineLabel = (value) => {
    if (value == null || value === '') return '';
    if (typeof value === 'number' && Number.isFinite(value)) {
        return new Date(value).toLocaleDateString();
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString();
    }
    return String(value);
};

const normalizeSparklineSeries = (series) => {
    const rows = Array.isArray(series) ? series : [];
    const baseTime = Date.UTC(2000, 0, 1);
    return rows.map((point, index) => {
        const rawX = point?.x ?? point?.date ?? point?.day ?? point?.label ?? point?.name ?? '';
        const parsedX = parseSparklineX(rawX);
        return {
            x: parsedX ?? (baseTime + (index * 86400000)),
            y: safeNum(point?.y ?? point?.value ?? point?.count ?? point),
            label: rawX || null
        };
    });
};



const ChartCard = (props) => {

    // console.log(props.data);


    const theme = useTheme();
    const headerBg = theme.palette.primary.main || '#1976d2';
    const isDarkHeader = (() => {
        if (!headerBg) return theme.palette.mode === 'dark';
        const raw = String(headerBg).replace('#', '');
        if (raw.length !== 6) return theme.palette.mode === 'dark';
        const r = parseInt(raw.slice(0, 2), 16) / 255;
        const g = parseInt(raw.slice(2, 4), 16) / 255;
        const b = parseInt(raw.slice(4, 6), 16) / 255;
        const luminance = (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
        return luminance < 0.55;
    })();
    const upColor = isDarkHeader
        ? (theme.palette.success.light || theme.palette.success.main || '#76ff03')
        : (theme.palette.success.dark || theme.palette.success.main || '#1b5e20');
    const downColor = isDarkHeader
        ? (theme.palette.error.light || theme.palette.error.main || '#ff8a80')
        : (theme.palette.error.dark || theme.palette.error.main || '#b71c1c');
    const indicatorBorder = isDarkHeader ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.2)';
    const surfaceCardSx = props.reportStyle ? getReportSurfaceSx(theme) : getExecutiveGlassCardSx(theme);
    const currentSeries = normalizeSparklineSeries(props.data?.series);
    const previousSeries = normalizeSparklineSeries(props.previousdata?.series);



    var options = applyApexBrandGradient({
        chart: {
            width: "auto",
            parentHeightOffset: 0,
            type: "area",
            toolbar: false,
            sparkline: {
                enabled: true
            }
        },
        zoom: {
            enabled: false,
        },
        tooltip: {
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const value = series?.[seriesIndex]?.[dataPointIndex] ?? 0;
                const valueP = previousSeries?.[dataPointIndex]?.y ?? 0;

                // Format current and previous values
                const formatValue = (val) => {
                    switch (props.title) {
                        case 'COST':
                        case 'Revenue':
                        case 'Avg Transaction':
                        case 'ROAS':
                        case 'CPC':
                        case 'CPM':
                            if (props.title === 'ROAS') {
                                return `${Number(val).toFixed(2)}x`;
                            }
                            return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        case 'CTR':
                        case 'Conversion Rate':
                            return `${Number(val).toFixed(2)}%`;
                        default:
                            return Number(val).toLocaleString();
                    }
                };

                const point = w.config.series?.[0]?.data?.[dataPointIndex];
                const label = formatSparklineLabel(point?.label ?? point?.x);
                const rows = [
                    { label: 'Current', value: formatValue(value) },
                    { label: 'Previous', value: formatValue(valueP) },
                ];

                return renderTrendTooltip(theme, {
                    title: label,
                    rows,
                    minWidth: 220
                });
            }
        },
        fill: {
            colors: [theme.palette.primary.main, darkenHexColor(theme.palette.primary.main, 50)]
        },
        stroke: {
            curve: 'smooth',
            width: 1,
            colors: [darkenHexColor(theme.palette.primary.main, 25)]
        },
        grid: {
            show: false,
            padding: {
                left: -2,
                right: 0,
                bottom: 0,
                top: 0
            }
        },
        dataLabels: {
            enabled: false
        },
        series: [
            {
                name: "Current",
                data: currentSeries
            }
        ],
        yaxis: {
            labels: {
                show: false,
            }
        },
        xaxis: {
            type: "datetime",
            axisTicks: {
                show: false
            },
            tooltip: {
                enabled: false
            },
            labels: {
                floating: true,
                show: false
            }
        }
    }, theme, 'area', { opacityFrom: 0.36, opacityTo: 0.08 });









    //  console.log(props);
    if (props.reportStyle) options = styleReportChart(theme, { ...options, chart: { ...options.chart, type: 'line' }, colors: [reportColors.revenue, reportColors.categories[5]], stroke: { width: 2, curve: 'straight' } });

    var countWrite = null;


    switch (props.title) {
        case 'COST':
            countWrite = <CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={(props.data.count !== null ? props.data.count : 0)} displayType={'text'} thousandSeparator={true} prefix={'$'} />;

            break;
        case 'Revenue':
            countWrite = <CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={(props.data.count !== null ? props.data.count : 0)} displayType={'text'} thousandSeparator={true} prefix={'$'} />;

            break;
        case 'Avg Transaction':
            countWrite = <CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={(props.data.count !== null ? props.data.count : 0)} displayType={'text'} thousandSeparator={true} prefix={'$'} />;

            break;
        case 'ROAS':
            countWrite = <NumericFormat decimalScale={2} fixedDecimalScale={true} value={(props.data.count !== null ? props.data.count : 0)} displayType="text" suffix="x" />;

            break;
        case 'CTR':
            countWrite = <NumericFormat value={(props.data.count !== null ? (props.data.count).toFixed(2) : 0)} displayType="text" suffix="%" />

            break;
        case 'Conversion Rate':
            countWrite = <NumericFormat value={(props.data.count !== null ? (props.data.count).toFixed(2) : 0)} displayType="text" suffix="%" />

            break;
        case 'CPC':
            countWrite = <CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={(props.data.count !== null ? props.data.count : 0)} displayType={'text'} thousandSeparator={true} prefix={'$'} />;

            break;
        case 'CPM':
            countWrite = <CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={(props.data.count !== null ? props.data.count : 0)} displayType={'text'} thousandSeparator={true} prefix={'$'} />;

            break;
        default:
            countWrite = <NumericFormat decimalScale={0} value={(props.data.count !== null ? props.data.count : 0)} displayType={'text'} thousandSeparator={true} />

            break;
    }






    return (
        <Box sx={{ flexGrow: 1, flex: '1 1 0%', minWidth: 0 }}>

            {(props ?

                <Tooltip
                    title={<div><Typography style={{ fontWeight: 900 }}>{props.title}:</Typography><Typography>{props.explain}</Typography></div>}
                    placement="top"
                    arrow
                    disableHoverListener={!props.explain}
                >
                    <Card
                        elevation={0}
                        sx={{
                            ...surfaceCardSx,
                            borderRadius: { xs: 3.25, md: 4 },
                            overflow: 'hidden',
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                            ...props.sx
                        }}
                    >
                    <CardHeader
                        subheader={props.title}
                        title={
                            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                                <Box sx={{ flexGrow: '1', fontSize: '1.25rem' }}>{countWrite}</Box>
                                <Box sx={{ fontSize: '.75rem' }}>
                                    {(() => {
                                        if (props.reportStyle && props.data?.delta == null && safeNum(props.previousdata?.count) <= 0) return null;
                                        const delta = Number(props.delta ?? 0);
                                        const isDown = delta < 0;
                                        const indicatorColor = isDown ? downColor : upColor;
                                        return (
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.25,
                                                    px: 0.5,
                                                    py: '1px',
                                                    borderRadius: 1,
                                                backgroundColor: alpha('#ffffff', 0.9),
                                                border: `1px solid ${indicatorBorder}`,
                                                color: indicatorColor
                                            }}
                                            >
                                                <Box component="span" sx={{ fontWeight: 700, lineHeight: 1 }}>
                                                    <NumericFormat decimalScale={2} value={props.delta} displayType="text" suffix="%" />
                                                </Box>
                                                {isDown ? (
                                                    <ArrowDownwardOutlined sx={{ fontSize: '1rem', color: indicatorColor }} />
                                                ) : (
                                                    <ArrowUpwardOutlined sx={{ fontSize: '1rem', color: indicatorColor }} />
                                                )}
                                            </Box>
                                        );
                                    })()}
                                </Box>
                            </Box>

                        }
                        subheaderTypographyProps={{ style: { color: alpha(theme.palette.text.primary, 0.72), marginLeft: theme.spacing(1), fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 } }}
                        sx={{
                            px: 1.2,
                            py: 1.1,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.secondary.main, 0.16)} 100%)`,
                            color: theme.palette.text.primary,
                            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
                        }}
                    ></CardHeader>

                    {(!props.hideChart ?
                        <CardContent
                            style={{ padding: 0, paddingTop: theme.spacing(1) }}
                        >
                            <Chart type={props.reportStyle ? 'line' : 'area'} series={options.series} options={options} />
                        </CardContent>
                        : '')}

                    </Card>
                </Tooltip>

                : '')}
        </Box>
    );
};

ChartCard.propTypes = {

};

export default ChartCard;
